import {
    ComponentProps,
    ComponentPropsWithoutRef,
    ForwardedRef,
    ReactNode,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { BoxIcon, BoxIconProps } from '@givebutter/primitives/box-icon';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ITableEmptyContext, TableContext, TableEmptyContext } from 'components/table/TableContext';
import { isTableEmpty } from 'components/table/util';
import { twMerge } from 'tailwind-merge';

interface TableEmptyProps extends ComponentPropsWithoutRef<'div'>, ITableEmptyContext {}

const TableEmpty = forwardRef((props: TableEmptyProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { variant = 'primary', className = '', children, ...rest } = props;
    const { table } = useContext(TableContext);

    const providerProps = useMemo(
        () => ({
            variant,
        }),
        [variant],
    );

    if (table && !isTableEmpty(table)) {
        return null;
    }

    return (
        <TableEmptyContext.Provider value={providerProps}>
            <div className="flex justify-center">
                <div
                    ref={ref}
                    className={twMerge(
                        'flex max-w-sm flex-col items-center justify-center gap-2 p-12',
                        className,
                    )}
                    {...rest}
                >
                    {children}
                </div>
            </div>
        </TableEmptyContext.Provider>
    );
});

interface TableEmptyWhenNotSearchingProps {
    children: ReactNode;
}

const TableEmptyWhenNotSearching = (props: TableEmptyWhenNotSearchingProps) => {
    const { children } = props;
    const { searchTerm, isFiltered } = useContext(TableContext);
    const isEmptySearch = !!searchTerm && searchTerm.length > 0;

    if (isEmptySearch || !!isFiltered) {
        return null;
    }

    return <>{children}</>;
};

interface TableEmptyIconProps extends Omit<BoxIconProps, 'size'> {}
const TableEmptyIcon = (props: TableEmptyIconProps) => {
    const { className = '', children, ...rest } = props;
    const { variant } = useContext(TableEmptyContext);
    const { searchTerm } = useContext(TableContext);
    const isEmptySearch = !!searchTerm && searchTerm.length > 0;

    return (
        <BoxIcon
            size="lg"
            variant={variant === 'primary' ? 'primary' : 'secondary'}
            className={className}
            {...rest}
        >
            {isEmptySearch || !children ? <FontAwesomeIcon icon={faSearch} /> : children}
        </BoxIcon>
    );
};

interface TableEmptyTitleProps extends ComponentProps<'p'> {}

const TableEmptyTitle = (props: TableEmptyTitleProps) => {
    const { className = '', children, ...rest } = props;
    const { variant } = useContext(TableEmptyContext);
    const { searchTerm, isFiltered } = useContext(TableContext);
    const isEmptySearch = !!searchTerm && searchTerm.length > 0;

    return (
        <p
            className={twMerge(
                'text-center font-medium text-gray-900',
                variant === 'primary' ? 'text-gray-900' : 'text-gray-600',
                className,
            )}
            {...rest}
        >
            {isEmptySearch || !!isFiltered || !children ? (
                <>No results found {isEmptySearch ? `for "${searchTerm}"` : 'with these filters'}</>
            ) : (
                children
            )}
        </p>
    );
};

interface TableEmptyDescriptionProps extends ComponentProps<'p'> {}

const TableEmptyDescription = (props: TableEmptyDescriptionProps) => {
    const { className = '', children = <>Try adjusting your criteria</>, ...rest } = props;
    const { searchTerm, isFiltered } = useContext(TableContext);
    const isEmptySearchOrFilters = (!!searchTerm && searchTerm.length > 0) || !!isFiltered;

    return (
        <p className={twMerge('text-center text-sm text-gray-600', className)} {...rest}>
            {isEmptySearchOrFilters ? <>Try adjusting your search criteria</> : children}
        </p>
    );
};

const Empty = TableEmpty;
const EmptyIcon = TableEmptyIcon;
const EmptyTitle = TableEmptyTitle;
const EmptyDescription = TableEmptyDescription;
const EmptyWhenNotSearching = TableEmptyWhenNotSearching;

export {
    TableEmpty,
    TableEmptyIcon,
    TableEmptyTitle,
    TableEmptyDescription,
    TableEmptyWhenNotSearching,
    //
    Empty,
    EmptyIcon,
    EmptyTitle,
    EmptyDescription,
    EmptyWhenNotSearching,
};
